import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { getBaseById, cookNow, generateRecipeVariations, generateBaseRecommendations } from '../services/api';

const CookNowModal = ({ kernelId, isOpen, onClose, onSuccess }) => {
    const { user } = useAuth();
    
    const [kernel, setKernel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    
    // Form state
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [customIngredients, setCustomIngredients] = useState('');
    const [timeMins, setTimeMins] = useState(30);
    const [aiGeneratedIngredients, setAiGeneratedIngredients] = useState([]);
    const [generatingIngredients, setGeneratingIngredients] = useState(false);
    
    // Recipe variation selection state
    const [showVariationSelection, setShowVariationSelection] = useState(false);
    const [generatingVariations, setGeneratingVariations] = useState(false);
    const [recipeVariations, setRecipeVariations] = useState([]);
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [expandedVariations, setExpandedVariations] = useState(new Set());
    const [redoCount, setRedoCount] = useState(0);
    const [maxRedos] = useState(2);

    // Ingredients verification state
    const [showIngredientsVerification, setShowIngredientsVerification] = useState(false);
    const [verificationIngredients, setVerificationIngredients] = useState([]);
    const [generatingRecipe, setGeneratingRecipe] = useState(false);

    // Customization state
    const [showCustomization, setShowCustomization] = useState(false);
    const [customizedIngredients, setCustomizedIngredients] = useState([]);
    const [customizing, setCustomizing] = useState(false);

    // Generate AI-aligned ingredient recommendations based on kernel context
    const generateIngredientRecommendations = async (kernelData) => {
        if (!kernelData) return;
        
        console.log('🚀 Starting AI ingredient recommendations generation for kernel:', kernelData.title);
        setGeneratingIngredients(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            console.log('📤 Calling generateBaseRecommendations API...');
            const recommendations = await generateBaseRecommendations(kernelId, token);
            console.log('📥 Received recommendations:', recommendations);
            
            setAiGeneratedIngredients(recommendations.ingredientRecommendations || []);
        } catch (err) {
            console.error('❌ Error generating recommendations:', err);
            setError('Failed to generate recommendations. Please try again.');
            
            // Fallback to basic recommendations if AI fails
            const fallbackIngredients = [
                {
                    category: "Flavor Themes",
                    items: [
                        {
                            name: "Classic Herbs",
                            ingredients: ["basil", "oregano", "thyme", "rosemary"]
                        },
                        {
                            name: "Citrus Brightness",
                            ingredients: ["lemon", "lime", "orange", "vinegar"]
                        },
                        {
                            name: "Umami Boost",
                            ingredients: ["garlic", "onions", "mushrooms", "soy sauce"]
                        },
                        {
                            name: "Spicy Kick",
                            ingredients: ["chili peppers", "black pepper", "cayenne", "paprika"]
                        },
                        {
                            name: "Sweet Balance",
                            ingredients: ["honey", "maple syrup", "brown sugar", "fruits"]
                        },
                        {
                            name: "Creamy Elements",
                            ingredients: ["butter", "cream", "cheese", "yogurt"]
                        }
                    ]
                },
                {
                    category: "Substitutes & Alternatives",
                    items: [
                        {
                            name: "Dairy Alternatives",
                            description: "Replace dairy with plant-based options",
                            ingredients: ["coconut milk", "almond milk", "cashew cream", "nutritional yeast"]
                        },
                        {
                            name: "Grain Substitutes",
                            description: "Alternative grains for different textures",
                            ingredients: ["quinoa", "cauliflower rice", "zucchini noodles", "sweet potato"]
                        },
                        {
                            name: "Protein Swaps",
                            description: "Different protein sources for variety",
                            ingredients: ["tofu", "tempeh", "lentils", "chickpeas"]
                        },
                        {
                            name: "Oil Alternatives",
                            description: "Healthier cooking oil options",
                            ingredients: ["avocado oil", "coconut oil", "olive oil", "ghee"]
                        }
                    ]
                }
            ];
            
            setAiGeneratedIngredients(fallbackIngredients);
        } finally {
            setGeneratingIngredients(false);
        }
    };

    useEffect(() => {
        if (isOpen && kernelId) {
            fetchKernel();
        }
    }, [isOpen, kernelId]);

    const fetchKernel = async () => {
        try {
            setLoading(true);
            const token = await user.getIdToken();
            const kernelData = await getBaseById(kernelId, token);
            setKernel(kernelData);
            // Generate meal goals and ingredients after kernel data is fetched
            await generateIngredientRecommendations(kernelData);
        } catch (err) {
            console.error('Error fetching kernel:', err);
            setError('Failed to load kernel data');
        } finally {
            setLoading(false);
        }
    };

    const handleStartCooking = async (e) => {
        e.preventDefault();
        
        setGeneratingVariations(true);
        setError(null);

        try {
            // Use a default goal since meal goals are removed
            const finalGoal = `A delicious ${kernel?.title.toLowerCase() || 'meal'}`;
            
            // Combine selected theme names with custom ingredients
            const allIngredients = [...selectedIngredients];
            
            if (customIngredients.trim()) {
                const customIngredientList = customIngredients.split(',').map(i => i.trim()).filter(i => i);
                allIngredients.push(...customIngredientList);
            }

            const token = await user.getIdToken();
            const variations = await generateRecipeVariations(
                kernelId, 
                finalGoal, 
                allIngredients.join(', '), 
                timeMins, 
                token,
                selectedIngredients // Pass selected flavor themes
            );

            // Handle single recipe response
            const recipeVariations = variations.variations || [variations];
            setRecipeVariations(recipeVariations);
            setShowVariationSelection(true);
        } catch (err) {
            console.error('Error generating recipe:', err);
            setError('Failed to generate recipe. Please try again.');
        } finally {
            setGeneratingVariations(false);
        }
    };

    const handleVariationSelection = async () => {
        if (!selectedVariation) {
            setError('Please select a recipe variation');
            return;
        }

        // Initialize verification ingredients with the selected variation's ingredients
        setVerificationIngredients([...selectedVariation.ingredients]);
        setShowIngredientsVerification(true);
    };

    const handleBackToForm = () => {
        setShowVariationSelection(false);
        setShowIngredientsVerification(false);
        setSelectedVariation(null);
        setExpandedVariations(new Set());
        setRecipeVariations([]);
        setVerificationIngredients([]);
        setError(null);
    };

    const handleRedoVariations = async () => {
        if (redoCount >= maxRedos) return;
        
        setGeneratingVariations(true);
        setError(null);
        setRedoCount(prev => prev + 1);

        try {
            // Use a default goal since meal goals are removed
            const finalGoal = `A delicious ${kernel?.title.toLowerCase() || 'meal'}`;
            
            // Combine selected theme names with custom ingredients
            const allIngredients = [...selectedIngredients];
            
            if (customIngredients.trim()) {
                const customIngredientList = customIngredients.split(',').map(i => i.trim()).filter(i => i);
                allIngredients.push(...customIngredientList);
            }

            const token = await user.getIdToken();
            const variations = await generateRecipeVariations(
                kernelId, 
                finalGoal, 
                allIngredients.join(', '), 
                timeMins, 
                token,
                selectedIngredients // Pass selected flavor themes
            );

            // Handle single recipe response
            const recipeVariations = variations.variations || [variations];
            setRecipeVariations(recipeVariations);
            setSelectedVariation(null);
            setExpandedVariations(new Set());
        } catch (err) {
            console.error('Error regenerating recipe:', err);
            setError('Failed to regenerate recipe. Please try again.');
        } finally {
            setGeneratingVariations(false);
        }
    };

    const toggleIngredientVisibility = (index) => {
        const newExpanded = new Set(expandedVariations);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedVariations(newExpanded);
    };

    const toggleIngredient = (ingredient) => {
        setSelectedIngredients(prev => 
            prev.includes(ingredient) 
                ? prev.filter(i => i !== ingredient)
                : [...prev, ingredient]
        );
    };

    const handleConfirmCustomization = async () => {
        setCustomizing(true);
        setError(null);

        try {
            // Use a default goal since meal goals are removed
            const finalGoal = `A delicious ${kernel?.title.toLowerCase() || 'meal'}`;
            
            // Start with customized ingredients as the primary source
            let finalIngredients = [...customizedIngredients];
            
            // Add any additional ingredients from the original selection that aren't already in customized
            const additionalIngredients = [...selectedIngredients];
            if (customIngredients.trim()) {
                const customIngredientList = customIngredients.split(',').map(i => i.trim()).filter(i => i);
                additionalIngredients.push(...customIngredientList);
            }
            
            // Only add ingredients that aren't already in the customized list
            additionalIngredients.forEach(ingredient => {
                if (!finalIngredients.some(custom => 
                    custom.toLowerCase().includes(ingredient.toLowerCase()) || 
                    ingredient.toLowerCase().includes(custom.toLowerCase())
                )) {
                    finalIngredients.push(ingredient);
                }
            });

            console.log('Final ingredients for AI generation:', finalIngredients);

            const token = await user.getIdToken();
            const recipe = await cookNow(
                kernelId, 
                finalGoal, 
                finalIngredients.join(', '), 
                timeMins, 
                token, 
                selectedVariation
            );

            onSuccess(recipe.id || recipe.draftId);
            onClose();
        } catch (err) {
            console.error('Error starting cooking session:', err);
            setError('Failed to start cooking session. Please try again.');
        } finally {
            setCustomizing(false);
        }
    };

    const handleGenerateRecipe = async () => {
        setGeneratingRecipe(true);
        setError(null);

        try {
            // Use a default goal since meal goals are removed
            const finalGoal = `A delicious ${kernel?.title.toLowerCase() || 'meal'}`;
            
            // Use the verification ingredients as the final ingredients, with fallback to selected variation ingredients
            let finalIngredients = verificationIngredients.filter(ingredient => ingredient.trim() !== '');
            
            // If verification ingredients are empty, use the selected variation's ingredients
            if (finalIngredients.length === 0 && selectedVariation?.ingredients) {
                finalIngredients = selectedVariation.ingredients;
            }
            
            // If still empty, use a default set of ingredients
            if (finalIngredients.length === 0) {
                finalIngredients = ['Basic ingredients for ' + (kernel?.title.toLowerCase() || 'cooking')];
            }
            
            console.log('Final ingredients for recipe generation:', finalIngredients);
            console.log('Selected variation:', selectedVariation);
            console.log('Verification ingredients:', verificationIngredients);

            const token = await user.getIdToken();
            const recipe = await cookNow(
                kernelId, 
                finalGoal, 
                finalIngredients.join(', '), 
                timeMins, 
                token, 
                selectedVariation
            );

            onSuccess(recipe.id || recipe.draftId);
            onClose();
        } catch (err) {
            console.error('Error generating recipe:', err);
            setError('Failed to generate recipe. Please try again.');
        } finally {
            setGeneratingRecipe(false);
        }
    };

    const handleBackToVariations = () => {
        setShowIngredientsVerification(false);
        setVerificationIngredients([]);
        setError(null);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-6 mobile-safe">
            <div className="bg-neutral-0 hairline max-w-modal-sm md:max-w-modal-md lg:max-w-modal-lg w-full max-h-[95vh] md:max-h-[90vh] flex flex-col">
                {/* Fixed Header */}
                <div className="flex-shrink-0 bg-neutral-0 border-b border-neutral-200 p-3 md:p-4 flex justify-between items-center">
                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                        {kernel ? `Cook: ${kernel.title}` : 'Cook Now'}
                    </div>
                    <button 
                        onClick={onClose}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close touch-target"
                        style={{ border: 'none', background: 'transparent', padding: 0, margin: 0 }}
                    >
                        ✕
                    </button>
                </div>
                
                {/* Scrollable Content */}
                <div className="flex-1 overflow-auto p-3 md:p-6">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="font-mono text-sm text-neutral-900 opacity-60 mb-2">
                                <span className="animate-spin inline-block mr-2">⚡</span>
                                Loading kernel...
                            </div>
                            <div className="font-mono text-xs text-neutral-900 opacity-40">
                                This will take a second
                            </div>
                        </div>
                    ) : showIngredientsVerification ? (
                        <>
                            {/* Ingredients Verification Screen */}
                            <div className="mb-4 md:mb-6">
                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                    Verify and edit ingredients for your {selectedVariation?.title}
                                </div>
                                <div className="font-mono text-xs text-neutral-900 opacity-40">
                                    Make sure all ingredients are available and adjust as needed
                                </div>
                            </div>

                            {/* Error Display */}
                            {error && (
                                <div className="mb-4 md:mb-6 bg-neutral-0 minimal-padding">
                                    <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-1">ERROR</div>
                                    <div className="font-mono text-xs text-neutral-900">{error}</div>
                                </div>
                            )}

                            {/* Ingredients List */}
                            <div className="space-y-3 mb-4 md:mb-6">
                                {verificationIngredients.map((ingredient, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={ingredient}
                                            onChange={(e) => {
                                                const newIngredients = [...verificationIngredients];
                                                newIngredients[index] = e.target.value;
                                                setVerificationIngredients(newIngredients);
                                            }}
                                            className="flex-1 p-2 md:p-3 hairline bg-neutral-0 text-sm font-mono"
                                            placeholder="Ingredient"
                                        />
                                        <button 
                                            onClick={() => {
                                                setVerificationIngredients(verificationIngredients.filter((_, i) => i !== index));
                                            }} 
                                            className="px-2 py-2 text-red-600 hover:bg-red-50 hairline font-mono text-sm"
                                            title="Remove ingredient"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add Ingredient Button */}
                            <button 
                                onClick={() => setVerificationIngredients([...verificationIngredients, ''])} 
                                className="mb-4 md:mb-6 px-3 py-2 bg-neutral-50 text-neutral-900 hairline font-mono text-sm hover:bg-neutral-100 transition-colors"
                            >
                                + Add Ingredient
                            </button>

                            {/* Action Buttons */}
                            <div className="pt-4 md:pt-6">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        type="button"
                                        onClick={handleBackToVariations}
                                        className="flex-1 minimal-padding bg-neutral-0 text-neutral-900 font-mono text-xs uppercase tracking-wider hover:bg-neutral-50 transition-colors touch-target"
                                    >
                                        ← Back to Variations
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleGenerateRecipe}
                                        className="flex-1 minimal-padding bg-neutral-900 text-neutral-0 font-mono text-xs uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 touch-target"
                                        disabled={generatingRecipe || verificationIngredients.length === 0}
                                    >
                                        {generatingRecipe ? (
                                            <>
                                                <span className="animate-spin inline-block mr-2">⚡</span>
                                                GENERATING RECIPE...
                                            </>
                                        ) : (
                                            'GENERATE RECIPE →'
                                        )}
                                    </button>
                                </div>
                            </div>

                            {generatingRecipe && (
                                <div className="mt-4 md:mt-6 bg-neutral-0 minimal-padding">
                                    <div className="font-mono text-xs opacity-60 uppercase tracking-wider">
                                        Creating your {selectedVariation?.title}... This may take a few seconds.
                                    </div>
                                </div>
                            )}
                        </>
                    ) : showVariationSelection ? (
                        <>
                            {/* Recipe Variation Selection Screen - Redesigned for Design System */}
                            <div className="mb-4 md:mb-6 flex items-start justify-between">
                                <div>
                                    <div className="font-mono text-xs text-neutral-900 opacity-40">
                                        Attempt {redoCount + 1} of {maxRedos + 1}
                                    </div>
                                </div>
                                {/* Redo Icon Button */}
                                <button
                                    type="button"
                                    onClick={handleRedoVariations}
                                    disabled={redoCount >= maxRedos || generatingVariations}
                                    className={`w-8 h-8 flex items-center justify-center transition-opacity touch-target ${
                                        redoCount >= maxRedos || generatingVariations
                                            ? 'opacity-30 cursor-not-allowed'
                                            : 'opacity-60 hover:opacity-100'
                                    }`}
                                    title={redoCount >= maxRedos ? 'No more redos available' : 'Regenerate variations'}
                                >
                                    <span className="font-mono text-sm">🔄</span>
                                </button>
                            </div>

                            {/* Error Display for Variation Selection */}
                            {error && (
                                <div className="mb-4 md:mb-6 bg-neutral-0 minimal-padding">
                                    <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-1">ERROR</div>
                                    <div className="font-mono text-xs text-neutral-900">{error}</div>
                                </div>
                            )}

                            <div className="space-y-3">
                                {/* Recipe Variation Options - Single Border Design */}
                                {recipeVariations.map((variation, index) => (
                                    <div key={index} className={`hairline transition-all p-3 md:p-4 cursor-pointer ${
                                        selectedVariation === variation
                                            ? 'bg-neutral-900 text-neutral-0'
                                            : 'bg-neutral-0 text-neutral-900'
                                    }`} onClick={() => setSelectedVariation(variation)}>
                                        {/* Variation Content - All in one clickable container */}
                                        <div className="flex items-start justify-between mb-3 md:mb-4">
                                            <div className="flex-1">
                                                <div className="font-mono text-xs uppercase tracking-wider font-bold mb-2">
                                                    {variation.title}
                                                </div>
                                                <div className="font-mono text-xs opacity-80 mb-2">
                                                    {variation.description}
                                                </div>
                                                <div className="font-mono text-xs opacity-60 uppercase tracking-wider">
                                                    {variation.style} • {variation.ingredients.length} ingredients
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0 ml-3 md:ml-4 mt-1">
                                                <div className={`w-4 h-4 flex items-center justify-center ${
                                                    selectedVariation === variation
                                                        ? 'bg-neutral-0 text-neutral-900'
                                                        : 'bg-neutral-0 text-neutral-900'
                                                }`}>
                                                    {selectedVariation === variation && (
                                                        <span className="font-mono text-xs font-bold">✓</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Show Ingredients Button & Expanded Ingredients - No separate container */}
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent triggering selection when clicking ingredients
                                                toggleIngredientVisibility(index);
                                            }}
                                            className={`font-mono text-xs uppercase tracking-wider transition-opacity cursor-pointer touch-target ${
                                                selectedVariation === variation
                                                    ? 'text-neutral-0 opacity-80 hover:opacity-100'
                                                    : 'text-neutral-900 opacity-60 hover:opacity-100'
                                            }`}
                                        >
                                            {expandedVariations.has(index) ? '▼ Hide Ingredients' : '▶ Show Ingredients'}
                                        </div>

                                        {expandedVariations.has(index) && (
                                            <div className="mt-3 pt-3 hairline-t">
                                                <div className="font-mono text-xs uppercase tracking-wider opacity-60 mb-2">
                                                    Ingredients:
                                                </div>
                                                <div className="space-y-1">
                                                    {variation.ingredients.map((ingredient, ingredientIndex) => (
                                                        <div key={ingredientIndex} className="font-mono text-xs opacity-80">
                                                            • {ingredient}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Action Buttons */}
                            <div className="pt-4 md:pt-6">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        type="button"
                                        onClick={handleBackToForm}
                                        className="flex-1 minimal-padding bg-neutral-0 text-neutral-900 font-mono text-xs uppercase tracking-wider hover:bg-neutral-50 transition-colors touch-target"
                                    >
                                        ← Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleVariationSelection}
                                        className="flex-1 minimal-padding bg-neutral-900 text-neutral-0 font-mono text-xs uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 touch-target"
                                        disabled={generating || !selectedVariation}
                                    >
                                        {generating ? (
                                            <>
                                                <span className="animate-spin inline-block mr-2">⚡</span>
                                                GENERATING RECIPE...
                                            </>
                                        ) : (
                                            'VERIFY INGREDIENTS →'
                                        )}
                                    </button>
                                </div>
                            </div>

                            {generating && (
                                <div className="mt-4 md:mt-6 bg-neutral-0 minimal-padding">
                                    <div className="font-mono text-xs opacity-60 uppercase tracking-wider">
                                        Creating your {selectedVariation?.title}... This may take a few seconds.
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {kernel && (
                                <div className="mb-4 md:mb-6">
                                    <div className="opacity-60 mb-3 md:mb-4 text-sm">
                                        Get a personalized recipe for {kernel.title.toLowerCase()} based on your preferences.
                                    </div>
                                    
                                    <div className="bg-neutral-50 p-3 md:p-4 hairline mb-4 md:mb-6">
                                        <div className="font-mono text-xs uppercase tracking-wider">
                                            <strong>TECHNIQUE SLOTS:</strong> {Object.keys(kernel.slots || {}).join(', ')}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleStartCooking} className="space-y-6 md:space-y-8">
                                {/* Flavor Themes Selection */}
                                <div>
                                    <div className="mb-3 md:mb-4">
                                        <div className="font-mono uppercase text-sm tracking-wider text-neutral-900 mb-1">FLAVOR THEMES & SUBSTITUTES</div>
                                        <div className="text-xs text-neutral-900 opacity-60">Select flavor themes and substitute options for your {kernel?.title.toLowerCase() || 'dish'}</div>
                                    </div>
                                    
                                    {/* AI-generated flavor themes and substitutes */}
                                    {generatingIngredients ? (
                                        <div className="text-center py-4">
                                            <div className="font-mono text-xs text-neutral-900 opacity-60 mb-2">
                                                <span className="animate-spin inline-block mr-2">⚡</span>
                                                Loading flavor themes...
                                            </div>
                                            <div className="font-mono text-xs text-neutral-900 opacity-40">
                                                This will take a second
                                            </div>
                                        </div>
                                    ) : (
                                        aiGeneratedIngredients.map((category, categoryIndex) => (
                                            <div key={categoryIndex} className="mb-4 md:mb-6">
                                                <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-2">
                                                    {category.category}
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                    {category.items.map((item, itemIndex) => (
                                                        <button
                                                            key={itemIndex}
                                                            type="button"
                                                            onClick={() => toggleIngredient(item.name)}
                                                            className={`p-2 md:p-3 text-left text-xs font-mono hairline transition-colors touch-target ${
                                                                selectedIngredients.includes(item.name)
                                                                    ? 'bg-neutral-900 text-neutral-0 hover:bg-neutral-900' 
                                                                    : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                                            }`}
                                                        >
                                                            <div className="font-semibold mb-1">{item.name}</div>
                                                            {item.description && (
                                                                <div className="opacity-80 text-xs mb-1">{item.description}</div>
                                                            )}
                                                            <div className="opacity-60 text-xs">
                                                                {item.ingredients.slice(0, 3).join(', ')}
                                                                {item.ingredients.length > 3 && '...'}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}

                                    {/* Selected flavor themes display */}
                                    {selectedIngredients.length > 0 && (
                                        <div className="mb-3 md:mb-4 p-2 md:p-3 bg-neutral-50 hairline">
                                            <div className="font-mono text-xs uppercase tracking-wider mb-2">SELECTED THEMES:</div>
                                            <div className="flex flex-wrap gap-1">
                                                {selectedIngredients.map((theme, index) => (
                                                    <span 
                                                        key={index}
                                                        className="px-2 py-1 bg-neutral-900 text-neutral-0 text-xs font-mono cursor-pointer hover:opacity-60 touch-target"
                                                        onClick={() => toggleIngredient(theme)}
                                                        title="Click to remove"
                                                    >
                                                        {theme} ✕
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Custom ingredients input */}
                                    <div className="hairline-t pt-3 md:pt-4">
                                        <div className="font-mono uppercase text-xs tracking-wider opacity-60 mb-2">ADD SPECIFIC INGREDIENTS:</div>
                                        <textarea
                                            value={customIngredients}
                                            onChange={(e) => setCustomIngredients(e.target.value)}
                                            placeholder="Additional specific ingredients (comma-separated)..."
                                            className="w-full p-2 md:p-3 hairline bg-neutral-0 resize-none text-sm font-mono touch-target"
                                            rows={2}
                                        />
                                    </div>
                                </div>

                                {/* Time Budget */}
                                <div>
                                    <div className="mb-3 md:mb-4">
                                        <div className="font-mono uppercase text-sm tracking-wider text-neutral-900 mb-1">TIME BUDGET</div>
                                        <div className="text-xs text-neutral-900 opacity-60">How much time do you have for cooking?</div>
                                    </div>
                                    <div className="space-y-3 md:space-y-4">
                                        <input
                                            type="range"
                                            min="15"
                                            max="120"
                                            step="15"
                                            value={timeMins}
                                            onChange={(e) => setTimeMins(parseInt(e.target.value))}
                                            className="custom-slider"
                                        />
                                        <div className="flex justify-between font-mono text-xs text-neutral-900 opacity-60">
                                            <span>15 MIN</span>
                                            <span>45 MIN</span>
                                            <span>75 MIN</span>
                                            <span>120 MIN</span>
                                        </div>
                                        <div className="text-center">
                                            <span className="font-mono text-sm font-bold bg-neutral-50 px-2 md:px-3 py-1 hairline">{timeMins} MINUTES</span>
                                        </div>
                                    </div>
                                </div>

                                {error && (
                                    <div className="bg-red-50 p-3 md:p-4 hairline">
                                        <div className="text-red-900 font-mono text-sm">{error}</div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="hairline-t pt-4 md:pt-6">
                                    <button
                                        type="submit"
                                        className="w-full px-4 md:px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 touch-target"
                                        disabled={generatingVariations}
                                    >
                                        {generatingVariations ? (
                                            <>
                                                <span className="animate-spin inline-block mr-2">⚡</span>
                                                GENERATING RECIPE...
                                            </>
                                        ) : (
                                            'GENERATE RECIPE →'
                                        )}
                                    </button>
                                </div>
                            </form>

                            {generatingVariations && (
                                <div className="mt-4 md:mt-6 bg-neutral-50 p-3 md:p-4 hairline">
                                    <div className="font-mono text-xs opacity-60">
                                        Generating recipe for your {kernel?.title.toLowerCase() || 'recipe'}... This may take a few seconds.
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Customization Modal */}
                {showCustomization && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-neutral-0 max-w-md w-full max-h-[80vh] overflow-y-auto">
                            <div className="p-4 md:p-6">
                                <div className="mb-4 md:mb-6">
                                    <div className="font-mono uppercase text-sm tracking-wider text-neutral-900 mb-1">
                                        CUSTOMIZE INGREDIENTS
                                    </div>
                                    <div className="text-xs text-neutral-900 opacity-60">
                                        Edit the ingredients for your {selectedVariation?.title} before starting
                                    </div>
                                </div>

                                <div className="space-y-3 mb-4 md:mb-6">
                                    {customizedIngredients.map((ingredient, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={ingredient}
                                                onChange={(e) => {
                                                    const newIngredients = [...customizedIngredients];
                                                    newIngredients[index] = e.target.value;
                                                    setCustomizedIngredients(newIngredients);
                                                }}
                                                className="flex-1 p-2 md:p-3 hairline bg-neutral-0 text-sm font-mono"
                                                placeholder="Ingredient"
                                            />
                                            <button 
                                                onClick={() => {
                                                    setCustomizedIngredients(customizedIngredients.filter((_, i) => i !== index));
                                                }} 
                                                className="px-2 py-2 text-red-600 hover:bg-red-50 hairline font-mono text-sm"
                                                title="Remove ingredient"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <button 
                                    onClick={() => setCustomizedIngredients([...customizedIngredients, ''])} 
                                    className="mb-4 md:mb-6 px-3 py-2 bg-neutral-50 text-neutral-900 hairline font-mono text-sm hover:bg-neutral-100 transition-colors"
                                >
                                    + Add Ingredient
                                </button>

                                {error && (
                                    <div className="mb-4 md:mb-6 bg-red-50 p-3 md:p-4 hairline">
                                        <div className="text-red-900 font-mono text-sm">{error}</div>
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <button 
                                        onClick={() => setShowCustomization(false)} 
                                        className="flex-1 px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={handleConfirmCustomization} 
                                        disabled={customizing} 
                                        className="flex-1 px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50"
                                    >
                                        {customizing ? 'Starting...' : 'Start Cooking'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CookNowModal; 